import { cn, type UtilityClassNames } from '@s4l/utility-css'
import React, { CSSProperties } from 'react'
import styles from './index.module.css'

const TYPOGRAPHY_VARIANTS = {
  title: ['xxl', 'xl', 'l', 'm', 's', 'xs'],
  body: ['xl', 'l', 'm', 's'],
  caption: ['l', 'm', 's'],
} as const

export type TypographyVariant = keyof typeof TYPOGRAPHY_VARIANTS
export type TypographySizes<V extends TypographyVariant> = (typeof TYPOGRAPHY_VARIANTS)[V][number]

type BaseTypographyProps<V extends TypographyVariant, E extends React.ElementType> = {
  variant?: V
  size?: TypographySizes<V>
  className?: UtilityClassNames
  as?: E
  children: React.ReactNode
  ellipsis?: boolean
  clamp?: number
  bold?: boolean
  strike?: boolean
  underline?: boolean
  color?: CSSProperties['color']
} & Omit<React.ComponentPropsWithoutRef<E>, 'className'>

type TypographyProps<E extends React.ElementType = 'p'> = {
  [V in TypographyVariant]: BaseTypographyProps<V, E>
}[TypographyVariant]

export default function Typography<E extends React.ElementType = 'p'>({
  variant = 'body',
  size = 'm',
  as,
  children,
  className,
  ellipsis = false,
  clamp,
  bold = false,
  strike = false,
  underline = false,
  color = undefined,
  ...props
}: TypographyProps<E>) {
  const Component = as || 'span'
  const variantClass = `${variant}-${size}`

  return (
    <Component
      className={cn(variantClass, className, {
        [styles.ellipsis]: ellipsis,
        [styles.clamp]: !!clamp,
        [styles.bold]: bold,
        [styles.strike]: strike,
        [styles.underline]: underline,
      })}
      style={
        {
          '--clamp-lines': clamp,
          color,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </Component>
  )
}
